import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    Stack,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Alert,
    InputAdornment,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    ClearAll as ClearCacheIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const CustomerManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [paginationMetadata, setPaginationMetadata] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [formData, setFormData] = useState({
        // New customer fields
        cardSerialNumber: '',
        consumerNumber: '',
        sv_Number: '',
        sv_Date: '',
        sv_Number_DBC: '',
        sv_Date_DBC: '',
        no_Of_Cylinders: '',
        deposit_Rs: '',
        no_of_Regulators: '',  // Fixed typo: was 'Regulators', should be 'Regulrators' to match database
        no_of_Regulrators_Deposit: '',
        consumer_Name: '',
        address1: '',
        address2: '',
        distributors_Code: '',
        distributors_Name: '',
        distributors_Address1: '',
        distributors_Address2: '',
        distributors_Phone: '',
        distributors_State: '',
        distributors_City: '',
        distributors_PIN: '',
        issuing_Company: ''
    });
    const { hasPermission, user } = useAuth();

    // Local storage key for distributor information
    const DISTRIBUTOR_STORAGE_KEY = 'gas_passbook_distributor_info';

    // Load distributor information from localStorage
    const loadDistributorInfo = () => {
        try {
            const saved = localStorage.getItem(DISTRIBUTOR_STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.error('Error loading distributor info from localStorage:', error);
        }
        return null;
    };

    // Save distributor information to localStorage
    const saveDistributorInfo = (distributorData) => {
        try {
            const distributorInfo = {
                distributors_Code: distributorData.distributors_Code || '',
                distributors_Name: distributorData.distributors_Name || '',
                distributors_Address1: distributorData.distributors_Address1 || '',
                distributors_Address2: distributorData.distributors_Address2 || '',
                distributors_Phone: distributorData.distributors_Phone || '',
                distributors_State: distributorData.distributors_State || '',
                distributors_City: distributorData.distributors_City || '',
                distributors_PIN: distributorData.distributors_PIN || '',
                issuing_Company: distributorData.issuing_Company || '',
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem(DISTRIBUTOR_STORAGE_KEY, JSON.stringify(distributorInfo));
        } catch (error) {
            console.error('Error saving distributor info to localStorage:', error);
        }
    };

    // Clear cached distributor information
    const clearDistributorCache = () => {
        try {
            localStorage.removeItem(DISTRIBUTOR_STORAGE_KEY);
            setSuccess('Distributor cache cleared successfully');
        } catch (error) {
            console.error('Error clearing distributor cache:', error);
            setError('Failed to clear distributor cache');
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1); // Reset to first page when searching
            fetchCustomers(searchTerm, 1, itemsPerPage);
        }, 300); // 300ms delay

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Fetch customers when page or page size changes
    useEffect(() => {
        fetchCustomers(searchTerm, currentPage, itemsPerPage);
    }, [currentPage, itemsPerPage]);

    const fetchCustomers = async (search = '', page = currentPage, pageSize = itemsPerPage) => {
        try {
            // Set appropriate loading state
            if (search) {
                setSearchLoading(true);
            } else {
                setLoading(true);
            }
            
            // Use server-side pagination and search
            const params = {
                pageNumber: page,
                pageSize: pageSize
            };
            
            if (search) {
                params.search = search;
            }
            
            const response = await api.get('/api/customers', { params });
            
            // Handle paginated response structure
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                setCustomers(response.data.data);
                setPaginationMetadata(response.data.pagination || {
                    totalRecords: 0,
                    totalPages: 0,
                    currentPage: page,
                    pageSize: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false
                });
                setError('');
            } else if (response.data && Array.isArray(response.data)) {
                // Fallback for direct array response (backwards compatibility)
                setCustomers(response.data);
                setPaginationMetadata({
                    totalRecords: response.data.length,
                    totalPages: 1,
                    currentPage: 1,
                    pageSize: response.data.length,
                    hasNextPage: false,
                    hasPreviousPage: false
                });
                setError('');
            } else {
                setError('Invalid response format from server.');
                setCustomers([]);
            }
        } catch (error) {
            setError('Failed to fetch customers: ' + (error.response?.data?.message || error.message));
            setCustomers([]);
        } finally {
            setLoading(false);
            setSearchLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');
            
            // VERSION CHECK - If you see this in console, the new code is loaded
            console.log('?? CustomerManagement.js - Version: 2025-01-11-v2');
            
            const method = selectedCustomer ? 'PUT' : 'POST';
            const url = selectedCustomer ? `/api/customers/${selectedCustomer.id}` : '/api/customers';
            
            // Ensure we have a valid tenant ID
            const tenantId = user?.tenantId || user?.tenant?.id || 1; // Default to 1 if not available
            
            // Prepare data with proper formatting and PascalCase for C# model
            const customerData = {
                Id: selectedCustomer?.id || 0,
                TenantId: tenantId,
                // Consumer fields - match C# model property names exactly
                CardSerialNumber: formData.cardSerialNumber || null,
                ConsumerNumber: formData.consumerNumber || null,
                ConsumerName: formData.consumer_Name || '',
                // SV fields - USE CORRECT PROPERTY NAMES (SvNumber, not SV_Number)
                SvNumber: formData.sv_Number || null,
                SvDate: (formData.sv_Date && formData.sv_Date.trim() !== '') ? new Date(formData.sv_Date + 'T00:00:00.000Z').toISOString() : null,
                SvNumberDbc: formData.sv_Number_DBC || null,
                SvDateDbc: (formData.sv_Date_DBC && formData.sv_Date_DBC.trim() !== '') ? new Date(formData.sv_Date_DBC + 'T00:00:00.000Z').toISOString() : null,
                // Numeric fields
                NoOfCylinders: formData.no_Of_Cylinders ? parseInt(formData.no_Of_Cylinders) : null,
                DepositRs: formData.deposit_Rs ? parseFloat(formData.deposit_Rs) : null,
                NoOfRegulrators: formData.no_of_Regulrators ? parseInt(formData.no_of_Regulrators) : null,
                NoOfRegulratorsDeposit: formData.no_of_Regulrators_Deposit ? parseFloat(formData.no_of_Regulrators_Deposit) : null,
                // Address fields
                Address1: formData.address1 || null,
                Address2: formData.address2 || null,
                // Distributor fields
                DistributorsCode: formData.distributors_Code || null,
                DistributorsName: formData.distributors_Name || null,
                DistributorsAddress1: formData.distributors_Address1 || null,
                DistributorsAddress2: formData.distributors_Address2 || null,
                DistributorsPhone: formData.distributors_Phone || null,
                DistributorsState: formData.distributors_State || null,
                DistributorsCity: formData.distributors_City || null,
                DistributorsPin: formData.distributors_PIN || null,
                IssuingCompany: formData.issuing_Company || null,
                // Default values
                IsActive: true,
                CreatedDate: selectedCustomer?.createdDate || new Date().toISOString()
            };

            console.log('Form data dates before processing:');
            console.log('  sv_Date:', formData.sv_Date);
            console.log('  sv_Date_DBC:', formData.sv_Date_DBC);
            console.log('? Sending customer data with CORRECT property names:', customerData);
            console.log('? Check: SvNumber exists?', 'SvNumber' in customerData);
            console.log('? Check: SV_Number exists?', 'SV_Number' in customerData);
            
            await api({
                method: method.toLowerCase(),
                url: url,
                data: customerData
            });

            console.log('Customer saved successfully');
            
            // Save distributor information to cache for future use
            saveDistributorInfo(formData);
            
            setSuccess(selectedCustomer ? 'Customer updated successfully' : 'Customer added successfully');
            fetchCustomers(searchTerm, currentPage, itemsPerPage);
            resetForm();
        } catch (error) {
            // Handle detailed validation errors
            console.error('Save customer error:', error);
            let errorMessage = 'Error saving customer';
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Handle ASP.NET Core validation errors format
                if (errorData.errors && typeof errorData.errors === 'object') {
                    const validationMessages = [];
                    for (const field in errorData.errors) {
                        const fieldErrors = errorData.errors[field];
                        if (Array.isArray(fieldErrors)) {
                            validationMessages.push(`${field}: ${fieldErrors.join(', ')}`);
                        }
                    }
                    if (validationMessages.length > 0) {
                        errorMessage = `Validation errors: ${validationMessages.join('; ')}`;
                    }
                } else if (errorData.title) {
                    errorMessage = errorData.title;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        // Load cached distributor info if available
        const cachedDistributor = loadDistributorInfo();
        
        setFormData({
            // New customer fields
            cardSerialNumber: '',
            consumerNumber: '',
            sv_Number: '',
            sv_Date: '',
            sv_Number_DBC: '',
            sv_Date_DBC: '',
            no_Of_Cylinders: '',
            deposit_Rs: '',
            no_of_Regulators: '',  // Fixed typo: was 'Regulators', should be 'Regulrators'
            no_of_Regulrators_Deposit: '',
            consumer_Name: '',
            address1: '',
            address2: '',
            // Load distributor information from cache if available
            distributors_Code: cachedDistributor?.distributors_Code || '',
            distributors_Name: cachedDistributor?.distributors_Name || '',
            distributors_Address1: cachedDistributor?.distributors_Address1 || '',
            distributors_Address2: cachedDistributor?.distributors_Address2 || '',
            distributors_Phone: cachedDistributor?.distributors_Phone || '',
            distributors_State: cachedDistributor?.distributors_State || '',
            distributors_City: cachedDistributor?.distributors_City || '',
            distributors_PIN: cachedDistributor?.distributors_PIN || '',
            issuing_Company: cachedDistributor?.issuing_Company || ''
        });
        setSelectedCustomer(null);
        setShowForm(false);
    };

    const editCustomer = (customer) => {
        console.log('Editing customer:', customer);
        setFormData({
            // Map from backend PascalCase to frontend form field names
            cardSerialNumber: customer.cardSerialNumber || customer.CardSerialNumber || '',
            consumerNumber: customer.consumerNumber || customer.ConsumerNumber || '',
            consumer_Name: customer.consumerName || customer.ConsumerName || customer.consumer_Name || customer.Consumer_Name || '',
            // SV fields - handle multiple possible property name variations
            sv_Number: customer.svNumber || customer.SvNumber || customer.sv_Number || customer.SV_Number || customer.sV_Number || '',
            sv_Date: customer.svDate ? customer.svDate.split('T')[0] : 
                    (customer.SvDate ? customer.SvDate.split('T')[0] : 
                    (customer.sv_Date ? customer.sv_Date.split('T')[0] : 
                    (customer.SV_Date ? customer.SV_Date.split('T')[0] : ''))),
            sv_Number_DBC: customer.svNumberDbc || customer.SvNumberDbc || customer.sv_Number_DBC || customer.SV_Number_DBC || customer.sV_Number_DBC || '',
            sv_Date_DBC: customer.svDateDbc ? customer.svDateDbc.split('T')[0] : 
                        (customer.SvDateDbc ? customer.SvDateDbc.split('T')[0] : 
                        (customer.sv_Date_DBC ? customer.sv_Date_DBC.split('T')[0] : 
                        (customer.SV_Date_DBC ? customer.SV_Date_DBC.split('T')[0] : ''))),
            // Numeric fields
            no_Of_Cylinders: customer.noOfCylinders || customer.No_Of_Cylinders || customer.no_Of_Cylinders || customer.No_Of_Cylinders || '',
            deposit_Rs: customer.depositRs || customer.DepositRs || customer.deposit_Rs || customer.Deposit_Rs || '',
            no_of_Regulrators: customer.noOfRegulrators || customer.NoOfRegulrators || customer.no_of_Regulrators || customer.No_of_Regulrators || '',
            no_of_Regulrators_Deposit: customer.noOfRegulratorsDeposit || customer.NoOfRegulratorsDeposit || customer.no_of_Regulrators_Deposit || customer.No_of_Regulrators_Deposit || '',
            // Address fields
            address1: customer.address1 || customer.Address1 || '',
            address2: customer.address2 || customer.Address2 || '',
            // Distributor fields
            distributors_Code: customer.distributorsCode || customer.DistributorsCode || customer.distributors_Code || customer.Distributors_Code || '',
            distributors_Name: customer.distributorsName || customer.DistributorsName || customer.distributors_Name || customer.Distributors_Name || '',
            distributors_Address1: customer.distributorsAddress1 || customer.DistributorsAddress1 || customer.distributors_Address1 || customer.Distributors_Address1 || '',
            distributors_Address2: customer.distributorsAddress2 || customer.DistributorsAddress2 || customer.distributors_Address2 || customer.Distributors_Address2 || '',
            distributors_Phone: customer.distributorsPhone || customer.DistributorsPhone || customer.distributors_Phone || customer.Distributors_Phone || '',
            distributors_State: customer.distributorsState || customer.DistributorsState || customer.distributors_State || customer.Distributors_State || '',
            distributors_City: customer.distributorsCity || customer.DistributorsCity || customer.distributors_City || customer.Distributors_City || '',
            distributors_PIN: customer.distributorsPin || customer.DistributorsPin || customer.distributors_PIN || customer.Distributors_PIN || '',
            issuing_Company: customer.issuingCompany || customer.IssuingCompany || customer.issuing_Company || customer.Issuing_Company || ''
        });
        setSelectedCustomer(customer);
        setShowForm(true);
    };

    const deleteCustomer = async (id) => {
        if (window.confirm('Are you sure you want to delete this customer?')) {
            try {
                setLoading(true);
                await api.delete(`/api/customers/${id}`);
                setSuccess('Customer deleted successfully');
                fetchCustomers(searchTerm, currentPage, itemsPerPage);
            } catch (error) {
                setError('Error deleting customer');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleInputChange = (field) => (event) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    // Use server-side pagination - customers array is already paginated
    const filteredCustomers = Array.isArray(customers) ? customers : [];
    const paginatedCustomers = filteredCustomers; // No client-side pagination needed

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (event) => {
        const newPageSize = parseInt(event.target.value, 10);
        setItemsPerPage(newPageSize);
        setCurrentPage(1); // Reset to first page when items per page changes
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Customer Management
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
                
                {hasPermission('customers.create') && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowForm(!showForm)}
                        sx={{ mb: 3 }}
                    >
                        {showForm ? 'Cancel' : 'Add New Customer'}
                    </Button>
                )}
            </Box>

            {showForm && (
                <Card sx={{ mb: 4 }}>
                    <CardHeader
                        title={
                            <Typography variant="h6">
                                {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
                            </Typography>
                        }
                    />
                    <CardContent>
                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                {/* Consumer Information Section */}
                                <Stack spacing={2}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Consumer Information
                                    </Typography>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Card Serial Number"
                                            value={formData.cardSerialNumber}
                                            onChange={handleInputChange('cardSerialNumber')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Consumer Number"
                                            value={formData.consumerNumber}
                                            onChange={handleInputChange('consumerNumber')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    </Stack>
                                    
                                    <TextField
                                        fullWidth
                                        label="Consumer Name"
                                        value={formData.consumer_Name}
                                        onChange={handleInputChange('consumer_Name')}
                                        required
                                        variant="outlined"
                                        sx={{ maxWidth: '400px' }}
                                        inputProps={{ maxLength: 200 }}
                                    />
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Number of Cylinders"
                                            type="number"
                                            value={formData.no_Of_Cylinders}
                                            onChange={handleInputChange('no_Of_Cylinders')}
                                            variant="outlined"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Number of Regulators"
                                            type="number"
                                            value={formData.no_of_Regulrators}
                                            onChange={handleInputChange('no_of_Regulrators')}
                                            variant="outlined"
                                        />
                                    </Stack>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Deposit Amount (Rs)"
                                            type="number"
                                            step="0.01"
                                            value={formData.deposit_Rs}
                                            onChange={handleInputChange('deposit_Rs')}
                                            variant="outlined"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Regulators Deposit (Rs)"
                                            type="number"
                                            step="0.01"
                                            value={formData.no_of_Regulrators_Deposit}
                                            onChange={handleInputChange('no_of_Regulrators_Deposit')}
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Stack>
                                
                                {/* Address Details Section */}
                                <Stack spacing={2}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Address Details
                                    </Typography>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Address 1"
                                            value={formData.address1}
                                            onChange={handleInputChange('address1')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 500 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Address 2"
                                            value={formData.address2}
                                            onChange={handleInputChange('address2')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 500 }}
                                        />
                                    </Stack>
                                </Stack>

                                {/* SV Information Section */}
                                <Stack spacing={2}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        SV Information
                                    </Typography>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="SV Number"
                                            value={formData.sv_Number}
                                            onChange={handleInputChange('sv_Number')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="SV Date"
                                            type="date"
                                            value={formData.sv_Date}
                                            onChange={handleInputChange('sv_Date')}
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Stack>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="SV Number DBC"
                                            value={formData.sv_Number_DBC}
                                            onChange={handleInputChange('sv_Number_DBC')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="SV Date DBC"
                                            type="date"
                                            value={formData.sv_Date_DBC}
                                            onChange={handleInputChange('sv_Date_DBC')}
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Stack>
                                </Stack>

                                {/* Distributor Information Section */}
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            Distributor Information
                                        </Typography>
                                        {!selectedCustomer && loadDistributorInfo() && (
                                            <Chip 
                                                label="Auto-filled from cache" 
                                                size="small" 
                                                color="info" 
                                                variant="outlined"
                                                title="Distributor information loaded from last entry"
                                            />
                                        )}
                                    </Box>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Distributor Code"
                                            value={formData.distributors_Code}
                                            onChange={handleInputChange('distributors_Code')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 20 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Distributor Name"
                                            value={formData.distributors_Name}
                                            onChange={handleInputChange('distributors_Name')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 200 }}
                                        />
                                    </Stack>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Distributor Address 1"
                                            value={formData.distributors_Address1}
                                            onChange={handleInputChange('distributors_Address1')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 500 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Distributor Address 2"
                                            value={formData.distributors_Address2}
                                            onChange={handleInputChange('distributors_Address2')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 500 }}
                                        />
                                    </Stack>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Distributor Phone"
                                            value={formData.distributors_Phone}
                                            onChange={handleInputChange('distributors_Phone')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 20 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Distributor State"
                                            value={formData.distributors_State}
                                            onChange={handleInputChange('distributors_State')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Stack>
                                    
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Distributor City"
                                            value={formData.distributors_City}
                                            onChange={handleInputChange('distributors_City')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 100 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Distributor PIN"
                                            value={formData.distributors_PIN}
                                            onChange={handleInputChange('distributors_PIN')}
                                            variant="outlined"
                                            inputProps={{ maxLength: 10 }}
                                        />
                                    </Stack>
                                </Stack>

                                {/* Company Information Section */}
                                <Stack spacing={2}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Company Information
                                    </Typography>
                                    
                                    <TextField
                                        fullWidth
                                        label="Issuing Company"
                                        value={formData.issuing_Company}
                                        onChange={handleInputChange('issuing_Company')}
                                        variant="outlined"
                                        sx={{ maxWidth: '400px' }}
                                        inputProps={{ maxLength: 200 }}
                                    />
                                </Stack>

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        disabled={loading}
                                    >
                                        {selectedCustomer ? 'Update' : 'Save'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </Button>
                                    {!selectedCustomer && (
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            startIcon={<ClearCacheIcon />}
                                            onClick={clearDistributorCache}
                                            title="Clear cached distributor information"
                                        >
                                            Clear Cache
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Paper>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6">
                                Customers List
                            </Typography>
                            <Chip 
                                label={`Showing ${paginatedCustomers.length} of ${paginationMetadata.totalRecords} customers`}
                                color="primary" 
                                size="small" 
                            />
                            {searchTerm && (
                                <Chip 
                                    label={`Search: "${searchTerm}"`}
                                    variant="outlined"
                                    size="small" 
                                    color="primary"
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Per page</InputLabel>
                                <Select
                                    value={itemsPerPage}
                                    label="Per page"
                                    onChange={handleItemsPerPageChange}
                                >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Search customers..."
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, number, card serial, or SV number"
                                sx={{ minWidth: 300 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {searchLoading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <SearchIcon />
                                            )}
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && !searchLoading && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchTerm('')}
                                                title="Clear search"
                                                edge="end"
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    </Box>
                    
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Card Serial Number</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Consumer Name</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Consumer Number</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cylinders</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Regulators</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedCustomers.map((customer) => (
                                    <TableRow key={customer.id || customer.Id} hover>
                                        <TableCell>{customer.cardSerialNumber || customer.CardSerialNumber || '-'}</TableCell>
                                        <TableCell>{customer.consumer_Name || customer.Consumer_Name}</TableCell>
                                        <TableCell>{customer.consumerNumber || customer.ConsumerNumber || '-'}</TableCell>
                                        <TableCell>{customer.no_Of_Cylinders || customer.No_Of_Cylinders || 0}</TableCell>
                                        <TableCell>{customer.no_of_Regulrators || customer.No_of_Regulrators || 0}</TableCell>
                                        <TableCell align="center">
                                            {hasPermission('customers.edit') && (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => editCustomer(customer)}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {hasPermission('customers.delete') && (
                                                <IconButton
                                                    color="error"
                                                    onClick={() => deleteCustomer(customer.id || customer.Id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!Array.isArray(customers) && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="error">
                                                Error: Invalid data format received from server
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {Array.isArray(customers) && filteredCustomers.length === 0 && searchTerm && !searchLoading && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary">
                                                No customers found matching "{searchTerm}"
                                            </Typography>
                                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                                Try searching by name, consumer number, card serial number, or SV number
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {Array.isArray(customers) && customers.length === 0 && !searchTerm && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary">
                                                No customers found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination Controls */}
                    {paginationMetadata.totalRecords > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {((paginationMetadata.currentPage - 1) * paginationMetadata.pageSize) + 1}-{Math.min(paginationMetadata.currentPage * paginationMetadata.pageSize, paginationMetadata.totalRecords)} of {paginationMetadata.totalRecords} customers
                                {searchTerm && ` (search results)`}
                            </Typography>
                            <Pagination
                                count={paginationMetadata.totalPages}
                                page={paginationMetadata.currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default CustomerManagement;